import { Button, ButtonGroup, Checkbox, Field, RadioGroup, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, PageSection } from 'Components/Layout';
import { AdditionalVisualDescription } from 'Components/Worksheets/Core/Fields';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { SieveAdditionFieldSet } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Components/SieveAdditionFieldSet';
import { getSeparationSieveSizeIds } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { actions as psdActions } from 'Data/ReduxViewModels/PsdSieveAnalysis';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { ISieveSize, SieveSizeId, getById } from 'Data/ViewModels/Equipment/ISieveSize';
import { FormatTypes } from 'Lib/Format';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TestProcedure } from '../../types';
import { BsEnManageSeparationsButton } from '../Separation/ManageSeparationsButton';

let TestPreparationPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dispatch = useDispatch();

  const separatingSievesList = useSelector(
    combine(
      [
        pipe(createWorksheetSelector!('', false), getSeparationSieveSizeIds),
        createWorksheetSelector!('Equipment.AllSieveSizes'),
      ],
      (ids: SieveSizeId[], allSieves: ISieveSize[]) =>
        ids.length > 0 ? ids.map((id: SieveSizeId) => getById(allSieves, id)?.Caption ?? 'Not set').join(', ') : 'None'
    )
  );

  const isSeparationOn16_0mmSieve = useSelector(
    pipe(createWorksheetSelector!('', false), getSeparationSieveSizeIds, (n) => n.some((s) => s === '16_0'))
  );

  const isFirstSeparationOn16_0mmSieveOrEmpty = useSelector(
    pipe(createWorksheetSelector!('WholePortion.Separation.SeparatingSieveSizeId'), (n) => n === '16_0' || !n)
  );

  const testProcedureOptions = [
    { label: 'Standard', value: TestProcedure.Standard },
    {
      label: 'Annex A (≥ 31.5mm Material)',
      value: TestProcedure.AnnexA,
      disabled: !isFirstSeparationOn16_0mmSieveOrEmpty,
    },
    { label: 'Annex B (Material unsuitable for oven-drying)', value: TestProcedure.AnnexB },
  ];

  const [testConditionsEditable, setTestConditionsEditable] = useState(false);
  const submittingSeparations = useSelector((state) => state.psdSieveAnalysis.separationManagement.status.submitting);
  const [needToSaveMethodA, setNeedToSaveMethodA] = useState(false);

  const changeSieveSetSizeOption = ({ data }: any) => {
    if (data.value === TestProcedure.AnnexA && !isSeparationOn16_0mmSieve) {
      dispatch(psdActions.setTempSeparations({ separations: [] }));
      dispatch(psdActions.addTempSeparation({ index: 0 }));
      dispatch(psdActions.setTempSeparationValue({ index: 0, name: 'SeparatingSieveSizeId', value: '16_0' }));
      dispatch(psdActions.applyTempSeparationsToWorksheet());
      setNeedToSaveMethodA(true); //trigger save test Method
    } else {
      dispatch(actions.setValue({ name: 'data.Preparation.Method', value: data.value, sync: true }));
    }
  };

  //Save the test Method if Annex A is used and new separation on 16_0 was triggered
  //there is a need to do this in this way to avoid the synchronization errror
  useEffect(() => {
    if (!submittingSeparations && needToSaveMethodA) {
      dispatch(actions.setValue({ name: 'data.Preparation.Method', value: TestProcedure.AnnexA, sync: true }));
      setNeedToSaveMethodA(false);
    }
  }, [dispatch, needToSaveMethodA, submittingSeparations]);

  return (
    <PageSection heading="Test Preparation">
      <NamePrefixModifier appendNamePrefix="Preparation">
        {/* Editable fields */}
        <ListFieldSection visible={testConditionsEditable}>
          <AdditionalVisualDescription editable />
          <Field input={<Checkbox />} name="LightweightAggregate" />
        </ListFieldSection>

        {/* Read-only fields */}
        <ListFieldSection visible={!testConditionsEditable}>
          <AdditionalVisualDescription />
          <Field input={<TextOutput />} name="LightweightAggregate" format={{ type: FormatTypes.YesNo }} />
          <FieldSet>
            <ButtonGroup>
              <Button
                onClick={() => {
                  setTestConditionsEditable(true);
                }}
              >
                Change
              </Button>
            </ButtonGroup>
          </FieldSet>
        </ListFieldSection>

        <ListFieldSection>
          <Field
            input={<RadioGroup />}
            name="Method"
            options={testProcedureOptions}
            confirmText="Changing the test procedure after data entry has begun can lead to data loss. This cannot be undone, continue?"
            onChange={changeSieveSetSizeOption}
          />

          <SieveAdditionFieldSet />
          <FieldSet>
            <Field input={<TextOutput />} label="Separating Sieve(s)" value={separatingSievesList} />
            <div>
              <BsEnManageSeparationsButton />
            </div>
          </FieldSet>
        </ListFieldSection>
      </NamePrefixModifier>
    </PageSection>
  );
};

TestPreparationPageSection = withInjectedProps(TestPreparationPageSection);

export { TestPreparationPageSection };
export default TestPreparationPageSection;
